import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, DollarSign, TrendingUp, Percent, Landmark,
  CreditCard, AlertTriangle, ArrowUpRight, CheckCircle2,
  Clock, XCircle, Eye,
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar,
  XAxis, YAxis, CartesianGrid,
} from "recharts";

const COLORS = ["#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#0ea5e9", "#6366f1"];
const STATUS_COLORS = {
  paid: "bg-emerald-50 text-emerald-700",
  pending: "bg-amber-50 text-amber-700",
  failed: "bg-red-50 text-red-700",
  reversed: "bg-violet-50 text-violet-700",
  under_review: "bg-blue-50 text-blue-700",
};
const STATUS_ICONS = { paid: CheckCircle2, pending: Clock, failed: XCircle, reversed: ArrowUpRight, under_review: Eye };

function KPI({ label, value, sub, icon: Icon, color = "text-emerald-600" }) {
  return (
    <Card className="border-slate-200" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{label}</span>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
        <div className="font-heading text-2xl font-bold text-slate-900">{value}</div>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function FinanceDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [payments, setPayments] = useState([]);
  const [debtors, setDebtors] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [statusDialog, setStatusDialog] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [adminNotes, setAdminNotes] = useState("");

  const fetchDashboard = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/dashboard");
      setDashboard(data);
    } catch { /* ignore */ }
  }, []);

  const fetchPayments = useCallback(async (status) => {
    try {
      const url = status && status !== "all" ? `/finance/payments?status=${status}` : "/finance/payments";
      const { data } = await api.get(url);
      setPayments(data);
    } catch { /* ignore */ }
  }, []);

  const fetchDebtors = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/debtors");
      setDebtors(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    Promise.all([fetchDashboard(), fetchPayments(), fetchDebtors()])
      .finally(() => setLoading(false));
  }, [fetchDashboard, fetchPayments, fetchDebtors]);

  const handleFilterChange = (val) => {
    setPaymentFilter(val);
    fetchPayments(val);
  };

  const handleStatusUpdate = async () => {
    if (!statusDialog || !newStatus) return;
    try {
      await api.put(`/finance/payments/${statusDialog.id}/status`, { status: newStatus, admin_notes: adminNotes });
      toast.success(`Payment status updated to ${newStatus}`);
      setStatusDialog(null);
      setAdminNotes("");
      fetchPayments(paymentFilter);
      fetchDashboard();
      fetchDebtors();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Finance Dashboard">
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </DashboardLayout>
    );
  }

  const d = dashboard || {};
  const statusData = (d.by_status || []).map((s) => ({ name: s._id || "unknown", value: s.total, count: s.count }));
  const typeData = (d.by_type || []).map((t) => ({ name: t._id || "unknown", value: t.total, count: t.count }));

  return (
    <DashboardLayout title="Finance Dashboard">
      <div className="space-y-6" data-testid="finance-dashboard">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100" data-testid="finance-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="debtors" data-testid="tab-debtors">Debtors</TabsTrigger>
          </TabsList>

          {/* ============ OVERVIEW ============ */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <KPI label="Collected" value={`$${(d.total_collected || 0).toLocaleString()}`} icon={DollarSign} color="text-emerald-600" />
              <KPI label="Pending" value={`$${(d.total_pending || 0).toLocaleString()}`} icon={Clock} color="text-amber-600" />
              <KPI label="Total Billed" value={`$${(d.total_all || 0).toLocaleString()}`} icon={Landmark} color="text-slate-600" />
              <KPI label="Collection Rate" value={`${d.collection_rate || 0}%`} icon={Percent} color="text-sky-600" />
              <KPI label="Monthly Revenue" value={`$${(d.expected_monthly || 0).toLocaleString()}`} sub="From active leases" icon={TrendingUp} color="text-indigo-600" />
              <KPI label="Deposits Held" value={`$${(d.deposits_held || 0).toLocaleString()}`} icon={CreditCard} color="text-violet-600" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Payment by Status */}
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="font-heading text-base font-bold">Payments by Status</CardTitle></CardHeader>
                <CardContent>
                  {statusData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={statusData} cx="50%" cy="50%" outerRadius={95} dataKey="value" label={({ name, value }) => `${name}: $${value.toLocaleString()}`}>
                          {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-slate-400 py-12 text-sm">No payment data</p>
                  )}
                </CardContent>
              </Card>

              {/* Payment by Type */}
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="font-heading text-base font-bold">Payments by Type</CardTitle></CardHeader>
                <CardContent>
                  {typeData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={typeData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                        <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                        <Bar dataKey="value" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-slate-400 py-12 text-sm">No payment type data</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Payments */}
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="font-heading text-base font-bold">Recent Payments</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-bold uppercase">Reference</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Tenant</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Type</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Amount</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Status</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(d.recent_payments || []).map((p) => (
                      <TableRow key={p.id || p._id}>
                        <TableCell className="font-mono text-xs">{p.reference || "—"}</TableCell>
                        <TableCell className="text-sm">{p.user_name || "—"}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[11px]">{p.type}</Badge></TableCell>
                        <TableCell className="font-mono font-bold text-sm">${(p.amount || 0).toLocaleString()}</TableCell>
                        <TableCell><Badge className={STATUS_COLORS[p.status] || "bg-slate-100"}>{p.status}</Badge></TableCell>
                        <TableCell className="text-xs text-slate-400">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</TableCell>
                      </TableRow>
                    ))}
                    {(d.recent_payments || []).length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No payments yet</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ PAYMENTS ============ */}
          <TabsContent value="payments" className="mt-6 space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-600">Filter:</span>
              <Select value={paymentFilter} onValueChange={handleFilterChange}>
                <SelectTrigger className="w-40" data-testid="payment-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="reversed">Reversed</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="outline" className="font-mono">{payments.length} records</Badge>
            </div>

            <Card className="border-slate-200">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-bold uppercase">Reference</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Tenant</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Type</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Amount</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Status</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Date</TableHead>
                        <TableHead className="text-xs font-bold uppercase text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.map((p) => {
                        const SIcon = STATUS_ICONS[p.status] || Clock;
                        return (
                          <TableRow key={p.id || p._id} data-testid={`payment-row-${p.id || p._id}`}>
                            <TableCell className="font-mono text-xs">{p.reference || "—"}</TableCell>
                            <TableCell className="text-sm">{p.user_name || p.user_email || "—"}</TableCell>
                            <TableCell><Badge variant="outline" className="text-[11px]">{p.type}</Badge></TableCell>
                            <TableCell className="font-mono font-bold">${(p.amount || 0).toLocaleString()}</TableCell>
                            <TableCell>
                              <Badge className={`${STATUS_COLORS[p.status] || "bg-slate-100"} inline-flex items-center gap-1`}>
                                <SIcon className="h-3 w-3" /> {p.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-slate-400">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                data-testid={`update-payment-${p.id || p._id}`}
                                onClick={() => { setStatusDialog({ id: p.id || p._id, current: p.status, reference: p.reference }); setNewStatus(p.status); setAdminNotes(""); }}
                              >
                                Update Status
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {payments.length === 0 && (
                        <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No payments found</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Status Update Dialog */}
            <Dialog open={!!statusDialog} onOpenChange={(o) => !o && setStatusDialog(null)}>
              <DialogContent data-testid="payment-status-dialog">
                <DialogHeader>
                  <DialogTitle>Update Payment {statusDialog?.reference}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">New Status</label>
                    <Select value={newStatus} onValueChange={setNewStatus}>
                      <SelectTrigger data-testid="new-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                        <SelectItem value="reversed">Reversed</SelectItem>
                        <SelectItem value="under_review">Under Review</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Notes (optional)</label>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add admin notes..."
                      data-testid="admin-notes"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setStatusDialog(null)}>Cancel</Button>
                  <Button onClick={handleStatusUpdate} disabled={newStatus === statusDialog?.current} data-testid="confirm-status-update">
                    Update
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ============ DEBTORS ============ */}
          <TabsContent value="debtors" className="mt-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <KPI label="Total Outstanding" value={`$${(debtors?.total_outstanding || 0).toLocaleString()}`} icon={AlertTriangle} color="text-red-500" />
              <KPI label="Debtors" value={debtors?.debtor_count || 0} icon={CreditCard} color="text-amber-600" />
              <KPI label="Avg. Owed" value={`$${debtors?.debtor_count > 0 ? Math.round((debtors?.total_outstanding || 0) / debtors.debtor_count).toLocaleString() : 0}`} icon={DollarSign} color="text-slate-600" />
            </div>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-base font-bold flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" /> Outstanding Balances
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-bold uppercase">Tenant</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Email</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Pending Payments</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Amount Owed</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Types</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Oldest</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(debtors?.debtors || []).map((d, i) => (
                      <TableRow key={i} data-testid={`debtor-row-${i}`}>
                        <TableCell className="font-medium text-sm">{d.user_name || "—"}</TableCell>
                        <TableCell className="text-sm text-slate-600">{d.user_email || "—"}</TableCell>
                        <TableCell><Badge variant="outline" className="font-mono">{d.payment_count}</Badge></TableCell>
                        <TableCell className="font-mono font-bold text-red-600">${(d.total_owed || 0).toLocaleString()}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(d.types || []).map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-slate-400">{d.oldest ? new Date(d.oldest).toLocaleDateString() : "—"}</TableCell>
                      </TableRow>
                    ))}
                    {(debtors?.debtors || []).length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-emerald-500 font-medium">No outstanding balances</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
